import { Component, OnInit, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { FormArray, FormBuilder, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatCardModule } from '@angular/material/card';
import { MatDividerModule } from '@angular/material/divider';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatNativeDateModule } from '@angular/material/core';
import { MatStepperModule } from '@angular/material/stepper';
import { MatChipsModule } from '@angular/material/chips';
import { MatTooltipModule } from '@angular/material/tooltip';
import { FormsModule } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { CustomersService } from '../../../services/customers.service';
import { IllustrationsService } from '../../../services/illustrations.service';
import { CreateIllustrationRequest, CategoryData, FeeData, IllustrationCompany, IllustrationCompanyOption, IllustrationClient } from '../../../interfaces';
import { AddNewButton } from '../../../shared/components/add-new-button/add-new-button';
import { NewCustomerDialogComponent } from '../../customers/new-customer-dialog/new-customer-dialog.component';

@Component({
  selector: 'app-add-new-illustration',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    FormsModule,
    MatButtonModule,
    MatIconModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatCardModule,
    MatDividerModule,
    MatDatepickerModule,
    MatNativeDateModule,
    MatStepperModule,
    MatChipsModule,
    MatTooltipModule,
    AddNewButton
  ],
  templateUrl: './add-new-illustration.html',
  styleUrls: ['./add-new-illustration.scss']
})
export class AddNewIllustration implements OnInit {
  // Stepper form groups
  companyForm: FormGroup;
  areasClientsForm: FormGroup;
  additionalSettingsForm: FormGroup;
  
  currentAreaIndex = 0;

  // Díjkezelés
  areaFees: { [key: number]: FeeData[] } = {}; // Területenkénti díjak

  // Data from backend
  companyOptions: IllustrationCompanyOption[] = [];
  companies: IllustrationCompany[] = [];
  // Detailed object for the currently selected company/individual
  selectedCompanyDetails: any = null;

  // Static clients removed - now using selectedCompanyDetails.relatedCustomers

  areas = [
    'KBH. fedezetek',
    'Magán EÜ',
    'Csoportos EÜ',
    'Privát nyugdíj',
    'Gyerekprogram',
    'Munkaerőmegtartás',
    'Balesetbiztosítás',
    'Céges nyugdíj',
    'Kocka'
  ];

  constructor(
    private fb: FormBuilder,
    private router: Router,
    private customersService: CustomersService,
    private illustrationsService: IllustrationsService,
    private dialog: MatDialog,
    private snackBar: MatSnackBar,
    private cdr: ChangeDetectorRef
  ) {
    this.companyForm = this.createCompanyForm();
    this.areasClientsForm = this.createAreasClientsForm();
    this.additionalSettingsForm = this.createAdditionalSettingsForm();
  }

  /**
   * A kiválasztott céghez tartozó ügyfelek lekérése + ideiglenes biztosítottak
   * Átalakítja a backend formátumot Client formátumra a kompatibilitás érdekében
   */
  get availableClients(): IllustrationClient[] {
    let clients: IllustrationClient[] = [];
    
    // Normál ügyfelek a kiválasztott cégből
    if (this.selectedCompanyDetails?.relatedCustomers) {
      clients = this.selectedCompanyDetails.relatedCustomers.map((customer: any) => ({
        id: customer.customerId || customer.id,
        name: customer.fullName || customer.fullname || customer.name,
        birthDate: customer.birthDate ? new Date(customer.birthDate) : new Date(),
        email: customer.emails?.[0]?.email || '',
        phone: customer.phones?.[0]?.phone || '',
        address: customer.address || ''
      }));
    }
    
    // Ideiglenes biztosítottak hozzáadása (név nélküli biztosítottak)
    const temporaryClients = (NewCustomerDialogComponent as any).temporaryInsuredPersons?.map((tempCustomer: any, index: number) => ({
      id: -(index + 1), // Negatív ID az ideigleneseknek
      name: tempCustomer.fullName,
      birthDate: tempCustomer.birthDate ? new Date(tempCustomer.birthDate) : new Date(),
      email: '',
      phone: '',
      address: '',
      isTemporary: true // Jelölés, hogy ez ideiglenes
    })) || [];
    
    return [...clients, ...temporaryClients];
  }

  /**
   * Ügyfél megjelenítési név formátázása: "Név (születési dátum)"
   */
  formatClientDisplay(customer: any): string {
    const name = customer.fullName || customer.fullname || customer.name;
    if (customer.birthDate) {
      const birthDate = new Date(customer.birthDate);
      const formattedDate = birthDate.toLocaleDateString('hu-HU');
      return `${name} (${formattedDate})`;
    }
    return name;
  }

  ngOnInit() {
    // Initialize with one area-client pair
    this.addAreaClient();
    // Load company options from backend
    this.loadCompanyOptions();
    
    // Inicializálás az első területhez
    this.initializeAreaFees(0);
  }

  onCompanySelected(option: IllustrationCompanyOption | null) {
    this.selectedCompanyDetails = null;
    if (!option) return;

    if (option.type === 'company') {
      this.customersService.getCompanyDetails(option.id).subscribe({
        next: (resp) => {
          // backend returns { success: True, data: { ... } }
          this.selectedCompanyDetails = resp?.data || resp;
        },
        error: (err) => {
          this.snackBar.open(
            `Hiba történt a cég részleteinek betöltése során: ${err.message || err}`,
            'Bezár',
            {
              duration: 5000,
              horizontalPosition: 'center',
              verticalPosition: 'top',
              panelClass: ['error-snackbar']
            }
          );
        }
      });
    } else {
      // individual
      this.customersService.getCustomerDetails(option.id).subscribe({
        next: (resp) => {
          this.selectedCompanyDetails = resp?.data || resp;
          
          // Individual esetén másolni kell az existingCustomer értéket a gyökér szintre
          if (this.selectedCompanyDetails?.relatedCustomers?.length > 0) {
            const individualData = this.selectedCompanyDetails.relatedCustomers[0];
            this.selectedCompanyDetails.existingCustomer = individualData.existingCustomer;
            this.selectedCompanyDetails.existing_customer = individualData.existingCustomer;
            this.selectedCompanyDetails.is_existing_customer = individualData.existingCustomer;
          }
        },
        error: (err) => {
          this.snackBar.open(
            `Hiba történt a magánszemély részleteinek betöltése során: ${err.message || err}`,
            'Bezár',
            {
              duration: 5000,
              horizontalPosition: 'center',
              verticalPosition: 'top',
              panelClass: ['error-snackbar']
            }
          );
        }
      });
    }
  }

  loadCompanyOptions() {
    this.customersService.getCompaniesWithCustomers().subscribe({
      next: (options) => {
        this.companyOptions = options;
      },
      error: (error) => {
        this.snackBar.open(
          `Hiba történt a cégek betöltése során: ${error.message || error}`,
          'Bezár',
          {
            duration: 5000,
            horizontalPosition: 'center',
            verticalPosition: 'top',
            panelClass: ['error-snackbar']
          }
        );
      }
    });
  }

  createCompanyForm(): FormGroup {
    return this.fb.group({
      selectedCompany: [null as IllustrationCompanyOption | null, Validators.required]
    });
  }

  createAreasClientsForm(): FormGroup {
    return this.fb.group({
      areaClients: this.fb.array([])
    });
  }

  createAdditionalSettingsForm(): FormGroup {
    return this.fb.group({
      sendDate: ['']
    });
  }



  get areaClientsArray(): FormArray {
    return this.areasClientsForm.get('areaClients') as FormArray;
  }

  createAreaClientForm(): FormGroup {
    return this.fb.group({
      area: ['', Validators.required],
      client: [[]], // Tömb a multi-select értékekhez
      notes: [''], // Megjegyzések mező
      fees: [[]], // Díjak tömb
      newFeeAmount: [''], // Új díj összeg
      newFeeCurrency: [''] // Új díj pénznem
    });
  }

  addAreaClient() {
    const areaClientForm = this.createAreaClientForm();
    const newIndex = this.areaClientsArray.length;
    this.areaClientsArray.push(areaClientForm);
    
    // Díjkezelés inicializálása az új területhez
    this.initializeAreaFees(newIndex);
  }

  removeAreaClient(index: number) {
    if (this.areaClientsArray.length > 1) {
      this.areaClientsArray.removeAt(index);
    }
  }

  onClientChange(areaIndex: number, clients: any[]) {
    const areaClientGroup = this.areaClientsArray.at(areaIndex);
    areaClientGroup.get('client')?.setValue(clients || []);
  }

  openNewClientDialog(areaIndex: number) {
    this.currentAreaIndex = areaIndex;
    
    const dialogRef = this.dialog.open(NewCustomerDialogComponent, {
      width: '800px',
      maxWidth: '95vw',
      maxHeight: '90vh',
      disableClose: false,
      data: {
        isEditMode: false,
        selectedCompanyId: this.companyForm.get('selectedCompany')?.value?.id,
        forceIndividualMode: true, // Csak magánszemély mód engedélyezve
        disableTypeSwitch: true, // Típus választó letiltva
        disableCompanySelection: true, // Cég kiválasztás letiltva
        preselectedCompany: this.companyForm.get('selectedCompany')?.value, // Előre kiválasztott cég
        showAnonymousInsuredCheckbox: true // Név nélküli biztosított checkbox megjelenítése
      },
      panelClass: 'crm-dialog'
    });

    dialogRef.afterClosed().subscribe(result => {
      // Név nélküli biztosított: result.success === true
      const isSuccessfulSave = (result?.action === 'save') || (result?.success === true);
      
      if (result && isSuccessfulSave) {
        // Ügyfél adatok kinyerése a visszatérési értékből
        let customerData = null;
        
        if (result.customer) {
          // Név nélküli biztosított esetén
          customerData = result.customer;
        } else if (result.data?.individual) {
          // Normál magánszemély létrehozás esetén
          customerData = result.data.individual;
          
          // Backend response-ból ID hozzáadása
          if (result.data.backendResponse?.customer_id) {
            customerData.customerId = result.data.backendResponse.customer_id;
          } else if (result.data.backendResponse?.id) {
            customerData.customerId = result.data.backendResponse.id;
          }
        }
        
        // Ha sikeresen kinyertük az ügyfél adatokat
        if (customerData) {
          const areaClientGroup = this.areaClientsArray.at(areaIndex);
          const currentClients = areaClientGroup.get('client')?.value || [];
          
          // Formázott ügyfél objektum létrehozása
          const newClient = {
            customerId: customerData.customerId || customerData.id,
            fullName: customerData.fullName || customerData.name,
            birthDate: customerData.birthDate,
            address: customerData.address || '',
            emails: customerData.email ? [{ email: customerData.email }] : [],
            phones: customerData.phone ? [{ phone: customerData.phone }] : [],
            existingCustomer: customerData.isExistingCustomer || customerData.existingCustomer || false,
            relationshipType: 'contact',
            isTemporary: result.isTemporary || false // Jelöljük meg, ha ideiglenes
          };
          
          // Frissítjük a selectedCompanyDetails.relatedCustomers tömböt
          if (this.selectedCompanyDetails && !this.selectedCompanyDetails.relatedCustomers) {
            this.selectedCompanyDetails.relatedCustomers = [];
          }
          if (this.selectedCompanyDetails) {
            // Új tömb létrehozása a meglévő elemekkel + új ügyfél (Angular change detection)
            const oldArray = this.selectedCompanyDetails.relatedCustomers || [];
            this.selectedCompanyDetails.relatedCustomers = [...oldArray, newClient];
            
            // Manuális change detection trigger
            this.cdr.detectChanges();
          }
          
          // Automatikusan hozzáadjuk az új ügyfelet a kiválasztott ügyfelek listájához
          areaClientGroup.get('client')?.setValue([...currentClients, newClient]);
          
          // Sikeres üzenet megjelenítése
          this.snackBar.open('Új ügyfél sikeresen hozzáadva és kiválasztva!', 'Bezár', {
            duration: 3000,
            panelClass: ['success-snackbar']
          });
        }
      }
    });
  }



  onSubmit() {
    if (this.isAllFormsValid()) {
      // Get current user ID first
      this.illustrationsService.getCurrentUserId().subscribe({
        next: (userId) => {
          this.saveIllustration(userId);
        },
        error: (error) => {
          this.snackBar.open(
            `Hiba történt a felhasználó azonosítása során: ${error.message || error}`,
            'Bezár',
            {
              duration: 5000,
              horizontalPosition: 'center',
              verticalPosition: 'top',
              panelClass: ['error-snackbar']
            }
          );
        }
      });
    } else {
      this.markAllFormsTouched();
    }
  }

  private saveIllustration(userId: number) {
    const selectedCompany = this.companyForm.value.selectedCompany;
    const sendDate = this.additionalSettingsForm.value.sendDate;
    const areaClientsData = this.areasClientsForm.value.areaClients;

    // Prepare categories data
    const categories: CategoryData[] = areaClientsData.map((areaClient: any, index: number) => {
      const areaFees = this.areaFees[index] || [];
      
      // Separate existing customers from new contacts
      const existingCustomers: number[] = [];
      const newContacts: any[] = [];

      if (areaClient.client && areaClient.client.length > 0) {
        areaClient.client.forEach((client: any, clientIndex: number) => {
          
          // Check multiple ID fields that might be used
          const clientId = client.id || client.customerId || client.customerid;
          
          if (clientId && clientId > 0) {
            // Existing customer with positive ID
            existingCustomers.push(clientId);
          } else {
            // New contact (negative ID or temp contact)
            newContacts.push({
              type: "contact_added",
              name: client.name || client.fullName || client.fullname,
              birthdate: client.birthDate ? this.formatDateForDb(client.birthDate) : null
            });
          }
        });
      }

      const categoryData = {
        notes: areaClient.notes || null,
        fees: areaFees,
        category: areaClient.area,
        other_entities: newContacts,
        customers: existingCustomers
      };
      
      return categoryData;
    });

    // Ha magánszemély van kiválasztva, akkor companyid legyen null
    const companyId = selectedCompany.type === 'individual' ? null : selectedCompany.id;
    
    const requestData: CreateIllustrationRequest = {
      companyid: companyId,
      userid: userId,
      senddate: this.formatDateForDb(sendDate),
      status: "Kiküldésre vár",
      categories: categories
    };
// Send to backend
    this.illustrationsService.createIllustration(requestData).subscribe({
      next: (response) => {
        // Sikeres mentés SnackBar
        this.snackBar.open(
          'Illusztráció sikeresen elmentve!', 
          'Bezárás', 
          {
            duration: 5000,
            horizontalPosition: 'center',
            verticalPosition: 'top',
            panelClass: ['success-snackbar']
          }
        );
        
        // Navigáció vissza az illustrations oldalra
        this.router.navigate(['/illustrations']);
      },
      error: (error) => {
        // Hiba SnackBar
        this.snackBar.open(
          'Hiba történt az illusztráció mentése során. Kérjük próbálja újra.', 
          'Bezárás', 
          {
            duration: 8000,
            horizontalPosition: 'center',
            verticalPosition: 'top',
            panelClass: ['error-snackbar']
          }
        );
      }
    });
  }

  private formatDateForDb(date: Date): string {
    if (!date) return new Date().toISOString().split('T')[0];
    return new Date(date).toISOString().split('T')[0];
  }

  isAllFormsValid(): boolean {
    const hasAreaClients = this.areaClientsArray.length > 0 && 
                          this.areaClientsArray.controls.every(control => control.valid);
    return this.companyForm.valid && 
           hasAreaClients && 
           this.additionalSettingsForm.valid;
  }

  isStep2Valid(): boolean {
    return this.areaClientsArray.length > 0 && 
           this.areaClientsArray.controls.every(control => {
             const area = control.get('area')?.value;
             const clients = control.get('client')?.value;
             return area && clients && clients.length > 0;
           });
  }

  goBack() {
    this.router.navigate(['/illustrations']);
  }

  private markAllFormsTouched() {
    this.markFormGroupTouched(this.companyForm);
    this.markFormGroupTouched(this.areasClientsForm);
    this.markFormGroupTouched(this.additionalSettingsForm);
  }

  private markFormGroupTouched(formGroup: FormGroup) {
    Object.keys(formGroup.controls).forEach(key => {
      const control = formGroup.get(key);
      control?.markAsTouched();
    });
  }

  // === DÍJKEZELŐ METÓDUSOK ===

  /**
   * Új díj hozzáadása egy területhez
   */
  addFee(areaIndex: number) {
    const areaClientControl = this.areaClientsArray.at(areaIndex);
    const amount = areaClientControl.get('newFeeAmount')?.value;
    const currency = areaClientControl.get('newFeeCurrency')?.value;
    
    if (amount && currency) {
      if (!this.areaFees[areaIndex]) {
        this.areaFees[areaIndex] = [];
      }
      
      this.areaFees[areaIndex].push({
        amount: parseFloat(amount),
        currency: currency
      });
      
      // Reset új díj mezők
      areaClientControl.get('newFeeAmount')?.setValue('');
      areaClientControl.get('newFeeCurrency')?.setValue('');
      
      // FormControl frissítése
      this.updateFeesFormControl(areaIndex);
    }
  }

  /**
   * Díj eltávolítása
   */
  removeFee(areaIndex: number, feeIndex: number) {
    if (this.areaFees[areaIndex]) {
      this.areaFees[areaIndex].splice(feeIndex, 1);
      this.updateFeesFormControl(areaIndex);
    }
  }

  /**
   * Díj formázása megjelenítéshez
   */
  formatFee(fee: FeeData): string {
    const symbols: { [key: string]: string } = {
      'HUF': 'Ft',
      'USD': '$',
      'EUR': '€'
    };
    
    return `${fee.amount.toLocaleString()} ${symbols[fee.currency] || fee.currency}`;
  }

  /**
   * FormControl frissítése a díjakkal
   */
  private updateFeesFormControl(areaIndex: number) {
    const areaClientControl = this.areaClientsArray.at(areaIndex);
    if (areaClientControl) {
      areaClientControl.get('fees')?.setValue(this.areaFees[areaIndex] || []);
    }
  }

  /**
   * Getter az elérhető ügyfelek listájához - mindig friss adatokat ad vissza
   */
  get availableRelatedCustomers() {
    const customers = this.selectedCompanyDetails?.relatedCustomers || [];
    return customers;
  }



  /**
   * Terület hozzáadásakor inicializálás
   */
  initializeAreaFees(areaIndex: number) {
    if (!this.areaFees[areaIndex]) {
      this.areaFees[areaIndex] = [];
    }
  }
}
